import { ILesson } from '@dtos/Course'
import { IFetchLessonProgressInput, ILessonsProgresses } from '@dtos/Enrollment'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useEnrollment } from '@hooks/useEnrollment'
import {
  ILessonProgressAttributesInputObject,
  useUpdateLessonProgress,
} from '@services/api/graphql/lessonProgresses'
import { MEDIA_TYPES } from '@constants/mediaTypes'
import { isUnauthorizedMutationError } from '@utils/apolloErrors'
import * as Sentry from '@sentry/react-native'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface IFinishLessonButtonStyle {
  iconColor: string
  icon: IconProp
  label: string
}

interface IUseLessonProgressParams {
  currentLesson: ILesson | null
  /**
   * Percentual de progresso a partir do qual o vídeo é marcado como concluído.
   * Nativo/mobile usa 75; web usa 100 (paridade com o legado web/core).
   */
  videoCompletionThreshold?: number
  /**
   * Chamado quando o servidor CONFIRMA a conclusão da aula (mutation
   * resolvida) — a atualização local do enrollment continua otimista.
   * Usado para reavaliar a navegação (ex.: liberar o botão "Próximo"
   * quando a próxima aula depende de progresso linear).
   */
  onLessonCompleted?: () => void
}

const AUTO_COMPLETE_ON_VIEW_TYPES = ['Text', 'Document', 'Image']

export function useLessonProgress({
  currentLesson,
  videoCompletionThreshold = 75,
  onLessonCompleted,
}: IUseLessonProgressParams) {
  const { handleUpdateLessonProgress } = useUpdateLessonProgress()
  const { enrollment, updateEnrollmentState, refetchEnrollment } =
    useEnrollment()
  // Visão do produtor é somente leitura: o servidor recusa a mutation (autoriza
  // por matrícula), então nem tentamos — evita erro no console e toast de falha.
  const { isPreview } = useAdminPreview()
  const [showLessonComplete, setShowLessonComplete] = useState(false)
  const reportedDenials = useRef(new Set<string>())

  // O core recusa a escrita com a mesma mensagem genérica para sessão sem
  // contexto, matrícula de outro aluno e progress card que não é da matrícula,
  // e o tick parcial rejeitava sem ninguém tratar: virava "ApolloError: Not
  // permitted to update this LessonProgress" no Sentry sem dizer QUAL
  // matrícula/aula/card (HET-4157). A recusa se repete a cada limiar do player,
  // então reportamos uma vez por aula, com os ids que permitem achar a causa.
  const handleProgressError = useCallback(
    (error: unknown, input: IFetchLessonProgressInput) => {
      console.error(error)
      if (!isUnauthorizedMutationError(error)) return

      const key = `${input.enrollmentId}:${input.lessonId}`
      if (reportedDenials.current.has(key)) return
      reportedDenials.current.add(key)

      Sentry.captureException(error, {
        tags: {
          type: 'lesson_progress_denied',
          has_progress_card: String(Boolean(input.progressCardId)),
        },
        extra: { ...input },
      })
    },
    [],
  )

  const isWebinar = currentLesson?.media?.type === 'WebinarHerospark'
  const isMediaToUpdateOnAccess = AUTO_COMPLETE_ON_VIEW_TYPES.includes(
    currentLesson?.media?.type ?? '',
  )
  // Embed-based video hosts (PandaVideos, generic "HTML livre") expose no time
  // API, so progress is counted on open — the same model as Text/Document/
  // Webinar lessons. Moviie is excluded: it reports real watch % via its player.
  const isOpenProgressVideo =
    currentLesson?.media?.type === 'Video' &&
    (currentLesson?.media?.host === MEDIA_TYPES.PANDAVIDEOS ||
      currentLesson?.media?.host === MEDIA_TYPES.EMBED)

  const currentLessonProgress =
    enrollment?.progressCard?.lessonsProgresses?.find(
      (lesson) => lesson.lessonId === currentLesson?.id,
    )

  const isLessonCompleted = useMemo(() => {
    return currentLessonProgress?.completed
  }, [currentLessonProgress?.completed])

  // Aula de texto/documento/imagem/webinar conclui só por abrir. Em preview
  // isso escreveria progresso sem nenhum clique do produtor.
  const shouldUpdateLessonProgressOnView =
    !isPreview &&
    (isWebinar || isMediaToUpdateOnAccess || isOpenProgressVideo) &&
    !isLessonCompleted

  const startMediaFrom = useMemo(() => {
    return isLessonCompleted ? 0 : currentLessonProgress?.currentVideoTime
  }, [currentLessonProgress?.currentVideoTime, isLessonCompleted])

  const finishLessonButtonStyle: IFinishLessonButtonStyle = useMemo(() => {
    return isLessonCompleted
      ? {
          iconColor: '$progress-complete',
          icon: ['fas', 'check-circle'],
          label: 'Concluído',
        }
      : {
          iconColor: '$icon-secondary',
          icon: ['fal', 'check-circle'],
          label: 'Concluir',
        }
  }, [isLessonCompleted])

  const updateEnrollmentProgress = useCallback(() => {
    if (!enrollment || !enrollment?.progressCard?.lessonsProgresses) return
    const loadedProgresses = enrollment.progressCard.lessonsProgresses

    // Aula que já constava concluída não conta de novo. O incremento cego
    // inflava o total e, quando o servidor respondia, o número "voltava" — o
    // aluno lê isso como conclusão que não colou / progresso perdido.
    if (isLessonCompleted) return

    const newLessonProgress = () => {
      if (currentLessonProgress) {
        return loadedProgresses.map((lessonProgress) => {
          if (lessonProgress.lessonId === currentLesson?.id) {
            return { ...lessonProgress, completed: true }
          }
          return lessonProgress
        })
      }
      return [
        ...loadedProgresses,
        {
          id: currentLesson?.id ?? 0,
          lessonId: currentLesson?.id ?? 0,
          progress: 100,
          currentVideoTime: 0,
          lastViewAt: new Date().toISOString(),
          completed: true,
        },
      ] as ILessonsProgresses[]
    }
    const newEnrollment = {
      ...enrollment,
      progressCard: {
        ...enrollment?.progressCard,
        completed: enrollment?.progressCard?.completed + 1,
        progress:
          ((enrollment?.progressCard?.completed + 1) /
            enrollment?.progressCard?.total) *
          100,
        lessonsProgresses: newLessonProgress(),
      },
    }
    updateEnrollmentState(newEnrollment)
  }, [
    enrollment,
    currentLesson?.id,
    currentLessonProgress,
    isLessonCompleted,
    updateEnrollmentState,
  ])

  const handleFinishLesson = useCallback(() => {
    if (isPreview) return
    if (!enrollment?.id || !currentLesson?.id) return
    setShowLessonComplete(true)

    const input = {
      lessonId: currentLesson?.id,
      enrollmentId: enrollment?.id,
      progressCardId: enrollment?.progressCard?.id ?? 0,
    }

    try {
      const updatePromise = handleUpdateLessonProgress(
        {
          completed: true,
          progress: 100,
        },
        input,
      )
      updateEnrollmentProgress()
      updatePromise
        .then(() => {
          // O gate do progresso linear é do servidor (HET-4001): a atualização otimista
          // acima só mexe em lessonsProgresses, então sem rebuscar a matrícula a próxima
          // aula continuaria com cadeado até a tela recarregar.
          refetchEnrollment()
          onLessonCompleted?.()
        })
        .catch((error) => handleProgressError(error, input))
    } catch (error) {
      console.error(error)
    }
  }, [
    currentLesson?.id,
    enrollment?.id,
    enrollment?.progressCard?.id,
    handleUpdateLessonProgress,
    handleProgressError,
    updateEnrollmentProgress,
    onLessonCompleted,
    refetchEnrollment,
    isPreview,
  ])

  useEffect(() => {
    if (shouldUpdateLessonProgressOnView) {
      handleFinishLesson()
    }
  }, [shouldUpdateLessonProgressOnView, handleFinishLesson])

  const handleUpdateProgress = useCallback(
    (progress: number, currentTime: number) => {
      if (isPreview) return
      if (!currentLesson?.id || !enrollment?.id || isLessonCompleted) return
      const completed = progress >= videoCompletionThreshold
      const attributes: ILessonProgressAttributesInputObject = {
        progress,
        currentVideoTime: currentTime,
      }
      // Tick parcial NÃO afirma que a aula está incompleta: ele só reporta onde o
      // player está. Mandar `completed: false` junto desmarcava no servidor uma
      // aula já concluída toda vez que a régua local estava desatualizada (lista
      // de progressos paginada, aula reaberta). Só o tick que cruza o limiar
      // conclui; abaixo dele a chave é OMITIDA e o `.compact` do servidor
      // preserva o valor gravado.
      if (completed) attributes.completed = true
      const input = {
        lessonId: currentLesson?.id,
        enrollmentId: enrollment?.id,
        progressCardId: enrollment?.progressCard?.id ?? 0,
      }
      // O `.catch` vale para TODO tick: sem ele a recusa do tick parcial virava
      // rejeição não tratada.
      handleUpdateLessonProgress(attributes, input)
        .then(() => {
          if (!completed) return
          refetchEnrollment()
          onLessonCompleted?.()
        })
        .catch((error) => handleProgressError(error, input))
      if (completed) updateEnrollmentProgress()
    },
    [
      currentLesson?.id,
      enrollment?.id,
      enrollment?.progressCard?.id,
      isLessonCompleted,
      videoCompletionThreshold,
      handleUpdateLessonProgress,
      handleProgressError,
      updateEnrollmentProgress,
      onLessonCompleted,
      refetchEnrollment,
      isPreview,
    ],
  )

  return {
    currentLessonProgress,
    isLessonCompleted,
    startMediaFrom,
    finishLessonButtonStyle,
    showLessonComplete,
    setShowLessonComplete,
    handleFinishLesson,
    handleUpdateProgress,
    updateEnrollmentProgress,
  }
}
